import type { LicenseFulfillmentDto } from '../../services/licenseComplianceService';

/**
 * Pace-based "at risk" projection for a license's current reporting period.
 *
 * Predictive counterpart to the reactive "Attention Required" signal in
 * `urgency.ts` (non-compliant + ≤30 days left). At-risk fires much earlier:
 * when earned hours lag the elapsed fraction of the period by more than a
 * buffer, or when a manual (attested) requirement is still unfulfilled late
 * in the period. Render sites suppress At Risk whenever Attention applies —
 * the reactive signal always wins visually.
 */

/** Minimum fraction of the period that must have elapsed before pace is judged (grace window). */
export const AT_RISK_MIN_ELAPSED_FRACTION = 0.25;
/** Allowed lag: earned fraction may trail elapsed fraction by up to this buffer before flagging. */
export const AT_RISK_PACE_BUFFER = 0.1;
/** Elapsed fraction at/after which an unfulfilled manual requirement flags risk. */
export const AT_RISK_MANUAL_ELAPSED_FRACTION = 0.75;

export interface AtRiskInput {
  periodStart: string;
  periodEnd: string;
  requiredTotalHours: number;
  earnedTotalHours: number;
  isCompliant: boolean;
  hasUnfulfilledManualRequirement: boolean;
}

export interface AtRiskResult {
  /** hoursPaceBehind || manualWindowRisk */
  atRisk: boolean;
  hoursPaceBehind: boolean;
  manualWindowRisk: boolean;
  /** Fraction of the reporting period elapsed, clamped to [0, 1]. */
  elapsedFraction: number;
}

const NOT_AT_RISK: AtRiskResult = {
  atRisk: false,
  hoursPaceBehind: false,
  manualWindowRisk: false,
  elapsedFraction: 0,
};

export function getAtRisk(input: AtRiskInput, now: Date = new Date()): AtRiskResult {
  const start = new Date(input.periodStart).getTime();
  const end = new Date(input.periodEnd).getTime();
  const total = end - start;
  if (!Number.isFinite(total) || total <= 0) return NOT_AT_RISK;

  const elapsedFraction = Math.min(1, Math.max(0, (now.getTime() - start) / total));

  const hoursPaceBehind =
    !input.isCompliant &&
    input.requiredTotalHours > 0 &&
    elapsedFraction >= AT_RISK_MIN_ELAPSED_FRACTION &&
    input.earnedTotalHours / input.requiredTotalHours < elapsedFraction - AT_RISK_PACE_BUFFER;

  // Manual requirements are binary — no pace to measure. Deliberately independent
  // of hours compliance: an hours-compliant license can still owe an attestation.
  const manualWindowRisk =
    input.hasUnfulfilledManualRequirement && elapsedFraction >= AT_RISK_MANUAL_ELAPSED_FRACTION;

  return {
    atRisk: hoursPaceBehind || manualWindowRisk,
    hoursPaceBehind,
    manualWindowRisk,
    elapsedFraction,
  };
}

/** At-risk for the user-facing fulfillment DTO (Reporting tab, licenses page, admin drawer). */
export function getLicenseFulfillmentAtRisk(
  license: LicenseFulfillmentDto,
  now?: Date
): AtRiskResult {
  return getAtRisk(
    {
      periodStart: license.periodStart,
      periodEnd: license.periodEnd,
      requiredTotalHours: license.requiredTotalHours,
      // Pace is judged on countable hours — excess over a credit limit doesn't
      // bring the license closer to its goal.
      earnedTotalHours: license.countableTotalHours ?? license.earnedTotalHours,
      isCompliant: license.isCompliant,
      hasUnfulfilledManualRequirement: license.subjectRequirements.some(
        (r) => r.targetKind === 'manual' && !r.isMet
      ),
    },
    now
  );
}
